import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Feature } from '@pubstack/common/src/rolesAndFeatures'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Icon, IconName } from './Icon'

export type TabProp = {
  label: string
  iconName?: IconName
  /**
   * Paints the icon alone, leaving the label as it was — for a marker that qualifies the tab rather than
   * recolouring it. `accentColor` is the other one, and it repaints the whole tab.
   */
  iconFill?: string
  active?: boolean
  disabled?: boolean
  trackingLabel: TrackingLabel
  feature?: Feature
  /**
   * Repaints the label and the selected indicator, for a tab that does not lead where the rest of the bar
   * leads — a Pubstack-only tab sitting next to the customer's own. Carried in both states on purpose: a
   * tab whose colour only appears once it is selected says nothing about where you are about to go.
   */
  accentColor?: string
  /**
   * How many rows the tab holds, rendered beside the label rather than baked into it.
   *
   * Separate from `label` because a tab is identified *by* its label — callers reverse-map a clicked tab
   * back to an id with it, and `active` is matched on it. Folding a live count into the string would
   * change a tab's identity every time its data changed.
   */
  count?: number
}

const TabIcon = styled(Icon)``

const TabCount = styled.span`
  margin-left: 6px;
  font-weight: 400;
  opacity: 0.7;
`

const TabWrapper = styled.div<{ active?: boolean; disabled?: boolean; accentColor?: string }>`
  ${Fonts.P1}
  font-weight: bold;
  padding: 0 10px 3px;
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${(props) => (props.disabled ? '#c4c4c4' : (props.accentColor ?? (props.active ? '#363636' : '#4c5774')))};
  position: relative;

  :before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    border-radius: 4px 4px 0 0;
    background-color: ${({ accentColor }) => accentColor ?? Colors.King};
    opacity: ${({ active }) => (active ? 1 : 0)};
  }

  ${({ active, disabled, accentColor }) =>
    !disabled &&
    css`
      :hover {
        color: ${accentColor ?? '#363636'};
        :before {
          opacity: ${active ? 1 : 0.1};
        }
      }
    `}

  ${TabIcon} {
    margin-right: 4px;
  }
`
const TabsWrapper = styled.div<{ fluid: boolean }>`
  border-bottom: 1px solid #e5e5e5;
  display: inline-flex;
  width: ${({ fluid }) => (fluid ? '100%' : 'auto')};

  ${TabWrapper} + ${TabWrapper} {
    margin-left: 8px;
  }
`

type TabOnClick = (_: TabProp) => void
const Tab = ({ tab, onClick }: { tab: TabProp; onClick?: TabOnClick }) => (
  <TabWrapper active={tab.active} disabled={tab.disabled} accentColor={tab.accentColor} onClick={() => !tab.disabled && onClick && onClick(tab)} data-tracking={tab.trackingLabel}>
    {tab.iconName && <TabIcon name={tab.iconName} width={'20px'} fill={tab.iconFill} />}
    {tab.label}
    {/* `!== undefined` rather than truthy: a queue that has emptied is worth showing as 0, and is the
        state a user watching a bulk run is waiting for. */}
    {tab.count !== undefined && <TabCount>{tab.count}</TabCount>}
  </TabWrapper>
)

const Tabs = ({ tabs, onClick, fluid = true }: { tabs: TabProp[]; onClick?: TabOnClick; fluid?: boolean }) => (
  <TabsWrapper fluid={fluid}>
    {tabs.map((tab, index) => (
      <Tab tab={tab} key={index} onClick={onClick} />
    ))}
  </TabsWrapper>
)

export { Tabs }
